import React, { useCallback, useReducer, useEffect } from "react";
import { useForm } from "react-hook-form";
import {getSensors, deleteSensor } from "../../services/sensors/sensorsService";
import CustomPaginator from "../../shared/components/paginator/CustomPaginator";
import ButtonNew from "../../shared/components/buttonNew/ButtonNew";
import InputSearch from "../../shared/components/inputSearch/InputSearch";
import SelectStatus from "../../shared/components/selectStatus/SelectStatus";
import ButtonFilter from "../../shared/components/buttonFilter/ButtonFilter";
import ButtonDownload from "../../shared/components/buttonDownload/ButtonDownload";
import ButtonEdit from "../../shared/components/buttonEdit/ButtonEdit";
// import ButtonDelete from "../../shared/components/buttonDelete/ButtonDelete";
import DeleteModal from "../../shared/components/deleteModal/DeleteModal";
import TableHeader from "../../shared/components/tableHeader/TableHeader";

const ITEMS_PER_PAGE = 10;
const TABLE_HEADERS = ['Id Sensor', 'Nombre Sensor', 'Estado', 'Acciones'];
const STATUS_OPTIONS = [
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false },
];

// Reducer
const initialState = {
    sensors: [],
    currentPage: 1,
    totalPages: 3,
    isLoading: false,
    error: null,
    deleteModalOpen: false,
    selectedSensorId: null,
    filters: { idSensorFilter: '', statusFilter: '' }
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_SENSORS':
            return { ...state, sensors: action.payload };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_PAGE':
            return { ...state, currentPage: action.payload };
        case 'SET_TOTAL_PAGES':
            return { ...state, totalPages: action.payload };
        case 'SET_FILTERS':
            return { ...state, filters: action.payload };
        case 'TOGGLE_DELETE_MODAL':
            return { 
                ...state, 
                deleteModalOpen: !state.deleteModalOpen,
                selectedSensorId: action.payload 
            };
        default:
            return state;
    }
}


// Components

const SensorTable = React.memo(({ sensors, onDelete, error }) => {
    if (error) {
        return (
            <tr className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
                <td colSpan={TABLE_HEADERS.length} className="text-center px-6 py-3 whitespace-nowrap">
                    Error obteniendo los datos
                </td>
            </tr>
        );
    }
    if (sensors.length === 0) {
        return (
            <tr className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
                <td colSpan={TABLE_HEADERS.length} className="text-center px-6 py-3 whitespace-nowrap">
                    No se encontraron registros
                </td>
            </tr>
        );
    }
    return sensors.map((sensor) => (
        <tr key={sensor.id} className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
            <th scope="row" className="px-6 py-3 font-medium whitespace-nowrap">
                {sensor.id}
            </th>
            
            <td className="px-6 py-3">
                {sensor.name}
            </td>

            <td className="px-6 py-3">
                {sensor.active ? <span>Activo</span> : <span>Inactivo</span>}
            </td>

            <td className="flex px-6 py-3">
                <ButtonEdit to={`/sensors-ad/${sensor.id}`}></ButtonEdit>
                {/* <ButtonDelete onClick={() => onDelete(sensor.id)} /> */}
            </td>
        </tr>
    ));
});

const FilterForm = React.memo(({ onSubmit, register }) => (
    <form onSubmit={onSubmit} className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 mb-2">
        <div className="flex items-center w-full md:w-1/3">
            <InputSearch
                placeholder="Id del sensor" 
                name="idSensorFilter" 
                id="idSensorFilter"
                {...register('idSensorFilter')}
            />
        </div>
        <div className="flex items-center w-full md:w-1/4">
            <SelectStatus
                id="statusFilter" 
                name="statusFilter"
                placeholder="Seleccionar estado"
                options={STATUS_OPTIONS}
                {...register('statusFilter')}
            />
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-end w-full md:w-1/3 space-y-3 sm:space-y-0 sm:space-x-4">
            <ButtonFilter type="submit" />
            <ButtonDownload />
        </div>
    </form>
));

const SensorsAd = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { register, handleSubmit } = useForm();

    const loadSensors = useCallback(async (filters = {}) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            dispatch({ type: 'SET_ERROR', payload: null });
            const data = await getSensors(state.currentPage, ITEMS_PER_PAGE, filters);
            dispatch({ type: 'SET_SENSORS', payload: data.content });
            dispatch({ type: 'SET_TOTAL_PAGES', payload: data.totalPages });
            
            if (data.totalPages < state.currentPage) {
                dispatch({ 
                    type: 'SET_PAGE', 
                    payload: data.totalPages === 0 ? 1 : data.totalPages 
                });
            }
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            console.error('Error fetching sensors:', error);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [state.currentPage]);

    useEffect(() => {
        const newFilters = {};
        if (state.filters.idSensorFilter) newFilters.idSensor = state.filters.idSensorFilter;
        if (state.filters.statusFilter) newFilters.active = state.filters.statusFilter;
        loadSensors(newFilters);
    }, [state.filters, loadSensors]);

    const handleFilterSubmit = handleSubmit((data) => {
        dispatch({ type: 'SET_FILTERS', payload: data });
    });

    const handlePageChange = useCallback((page) => {
        if (page === state.currentPage || page < 1 || page > state.totalPages) return;
        dispatch({ type: 'SET_PAGE', payload: page });
    }, [state.currentPage, state.totalPages]);

    const toggleDeleteModal = useCallback((sensorId) => {
        dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: sensorId });
    }, []);

    const handleDelete = async () => {
        try {
            const response = await deleteSensor(state.selectedSensorId);
            if (response) {
                dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: null });
                await loadSensors();
            }
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Error al eliminar el sensor' });
            console.error('Error al eliminar el sensor: ', error);
        }
    };

    return (
        <div className="flex flex-col p-3">
                
            <div className="flex max-sm:flex-col items-center justify-between mb-4">
                <h1 className="text-3xl font-semibold text-blue-600">Sensores</h1>
                    <ButtonNew to={'/sensors-ad/newSensor'}></ButtonNew>
            </div>

            <FilterForm onSubmit={handleFilterSubmit} register={register} />

            <hr className="my-2" />

            {state.isLoading ? (
                <div className="text-center py-4">Cargando...</div>
            ) : (
                <div className="relative overflow-x-auto shadow-lg sm:rounded-lg">
                    <table className="w-full text-sm text-left border border-gray-200">
                        <TableHeader headerOptions={TABLE_HEADERS}/>
                        <tbody>
                            <SensorTable 
                                sensors={state.sensors} 
                                onDelete={toggleDeleteModal}
                                error={state.error}
                            />
                        </tbody>
                    </table>
                </div>
            )}

            {!state.error && (
                <CustomPaginator 
                    currentPage={state.currentPage} 
                    totalPages={state.totalPages} 
                    handlePageChange={handlePageChange}
                />
            )}

            <DeleteModal
                isOpen={state.deleteModalOpen}
                toggleDeleteModal={() => toggleDeleteModal(null)}
                deleteModal={handleDelete}
                nameParameter="sensor"
            />
        </div>
    
  );
};

export default SensorsAd;